import React, { useRef, useState } from 'react';

export interface AskDeweyPanelProps {
  onSubmit: (query: string) => void;
  onClear: () => void;
  hasClearableState?: boolean;
  isLoading?: boolean;
}

const AskDeweyPanel: React.FC<AskDeweyPanelProps> = ({
  onSubmit,
  onClear,
  hasClearableState = false,
  isLoading = false,
}) => {
  const [query, setQuery] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const submitQuery = () => {
    const trimmed = query.trim();
    if (trimmed && !isLoading) {
      onSubmit(trimmed);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitQuery();
  };

  /**
   * A textarea does not submit its form on Enter, so submission is explicit.
   * Shift+Enter still inserts a newline, and an in-progress IME composition is
   * left alone so Enter can commit the candidate instead of sending the query.
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    submitQuery();
  };

  const handleClear = () => {
    setQuery('');
    onClear();
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    textareaRef.current?.focus();
  };

  const hasQuery = query.trim().length > 0;
  const canClear = hasQuery || hasClearableState;

  return (
    <form className="ask-dewey-panel" onSubmit={handleSubmit}>
      <textarea
        ref={textareaRef}
        className="ask-dewey-panel__input"
        placeholder="Who is in charge of updating the chart of accounts?"
        value={query}
        rows={1}
        onChange={(e) => {
          setQuery(e.target.value);
          e.currentTarget.style.height = 'auto';
          e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
        }}
        disabled={isLoading}
        onKeyDown={handleKeyDown}
        aria-label="Ask Dewey query"
      />

      <div className="ask-dewey-panel__actions">
        {canClear && (
          <button
            type="button"
            className="ask-dewey-panel__clear"
            onClick={handleClear}
            disabled={isLoading}
            title="Clear Ask Dewey"
            aria-label="Clear query"
          >
            <i className="modus-icons" aria-hidden="true">close</i>
          </button>
        )}
        <button
          type="submit"
          className="ask-dewey-panel__submit"
          disabled={!hasQuery || isLoading}
          title="Ask Dewey"
          aria-label="Submit query"
        >
          <i className="modus-icons" aria-hidden="true">{isLoading ? 'circle_notch' : 'search'}</i>
        </button>
      </div>
    </form>
  );
};

export default AskDeweyPanel;
